<template>
  <div class="employee-filter">
    <div class="filter-grid">
      <!-- Status Filter -->
      <div class="filter-item width-2">
        <h4 class="mb-2">{{ i18n.t('status') }}</h4>
        <div class="filter-options">
          <div class="flex items-center gap-2">
            <SmCheckbox
              v-model:model-value="localFilters.status.active"
              @update:model-value="updateFilters" />
            <span>{{ i18n.t('active') }}</span>
          </div>
          <div class="flex items-center gap-2">
            <SmCheckbox
              v-model:model-value="localFilters.status.terminated"
              @update:model-value="updateFilters" />
            <span>{{ i18n.t('terminated') }}</span>
          </div>
        </div>
      </div>

      <!-- Department Filter -->
      <div class="filter-item width-2">
        <h4 class="mb-2">{{ i18n.t('department') }}</h4>
        <SmSelect
          v-model:selection="localFilters.department"
          :options="departmentOptions"
          :label="i18n.t('selectDepartment')"
          :multiselect="true"
          searchable
          @update:selection="updateFilters"
          style="width: 100%" />
      </div>

      <!-- Position Filter -->
      <div class="filter-item width-2">
        <h4 class="mb-2">{{ i18n.t('position') }}</h4>
        <SmSelect
          v-model:selection="localFilters.position"
          :options="positionOptions"
          :label="i18n.t('selectPosition')"
          :multiselect="true"
          searchable
          @update:selection="updateFilters"
          style="width: 100%" />
      </div>

      <!-- Date Range Filters -->
      <div class="filter-item width-2">
        <h4 class="mb-2">{{ i18n.t('dateRange') }}</h4>
        <div class="date-range-filters">
          <div class="date-filter">
            <label class="date-label">{{ i18n.t('hiredAfter') }}</label>
            <el-date-picker
              v-model="localFilters.dateRange.hiredAfter"
              type="date"
              size="small"
              :placeholder="i18n.t('selectDate')"
              style="width: 100%"
              @change="updateFilters" />
          </div>
          <div class="date-filter">
            <label class="date-label">{{ i18n.t('hiredBefore') }}</label>
            <el-date-picker
              v-model="localFilters.dateRange.hiredBefore"
              type="date"
              size="small"
              :placeholder="i18n.t('selectDate')"
              style="width: 100%"
              @change="updateFilters" />
          </div>
        </div>
      </div>
    </div>

    <!-- Clear Filters Button -->
    <div class="filter-actions">
      <SmButton
        type="secondary"
        size="small"
        @click="clearFilters"
        :disabled="!hasActiveFilters">
        {{ i18n.t('clearFilters') }}
      </SmButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { ref, watch, computed } from 'vue'
  import { Employee } from '@/client'
  import type { Option } from '@/components/sm/SmSelect.vue'

  interface EmployeeFilters {
    status: {
      active: boolean
      terminated: boolean
    }
    department: Option<string>[]
    position: Option<string>[]
    dateRange: {
      hiredAfter: string | null
      hiredBefore: string | null
    }
  }

  const props = defineProps<{
    employees: Employee[]
    filter: EmployeeFilters
  }>()

  const emit = defineEmits<{
    'update:filter': [value: EmployeeFilters]
  }>()

  const i18n = useI18n()

  // Create a local copy of filters for two-way binding
  const localFilters = ref<EmployeeFilters>({ ...props.filter })

  // Watch for external changes to filter prop
  watch(
    () => props.filter,
    (newFilter) => {
      localFilters.value = { ...newFilter }
    },
    { deep: true }
  )

  // Helper function to get employee field from employee_external or application_specific
  const getEmployeeField = (employee: Employee, fieldName: string): any => {
    // Check in employee_external first
    if (employee.employee_external && employee.employee_external[fieldName]) {
      return employee.employee_external[fieldName]
    }
    // Check in application_specific
    if (employee.application_specific && employee.application_specific[fieldName]) {
      return employee.application_specific[fieldName]
    }
    return null
  }

  // Department options computed from employees
  const departmentOptions = computed(() => {
    const departments = new Set<string>()
    props.employees.forEach((employee) => {
      const dept = getEmployeeField(employee, 'department')
      if (dept) departments.add(dept)
    })
    return Array.from(departments).map((dept) => ({
      label: dept,
      value: dept,
    }))
  })

  // Position options computed from employees
  const positionOptions = computed(() => {
    const positions = new Set<string>()
    props.employees.forEach((employee) => {
      if (employee.position) positions.add(employee.position)
    })
    return Array.from(positions).map((pos) => ({
      label: pos,
      value: pos,
    }))
  })

  // Check if there are any active filters
  const hasActiveFilters = computed(() => {
    const filters = localFilters.value
    // Status filters
    if (!filters.status.active || !filters.status.terminated) return true
    // Department filter
    if (filters.department.length > 0) return true
    // Position filter
    if (filters.position.length > 0) return true
    // Date range filters
    if (filters.dateRange.hiredAfter || filters.dateRange.hiredBefore) return true
    return false
  })

  // Update filters and emit to parent
  function updateFilters() {
    emit('update:filter', { ...localFilters.value })
  }

  // Clear all filters
  function clearFilters() {
    localFilters.value = {
      status: {
        active: true,
        terminated: true,
      },
      department: [],
      position: [],
      dateRange: {
        hiredAfter: null,
        hiredBefore: null,
      },
    }
    updateFilters()
  }
</script>

<style scoped lang="scss">
  .employee-filter {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &.width-2 {
      grid-column: span 2;
    }
  }

  h4 {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--el-text-color-primary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .filter-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .date-range-filters {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .date-filter {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .date-label {
    font-size: 0.75rem;
    color: var(--el-text-color-secondary);
    font-weight: 500;
  }

  .filter-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    border-top: 1px solid var(--el-border-color-light);
  }
</style>

